import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Stethoscope, Clock, RefreshCw, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

const isDev = import.meta.env.DEV;

export const ResetPasswordForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Session management state
  const [sessionValid, setSessionValid] = useState<boolean | null>(null);
  const [sessionTimeLeft, setSessionTimeLeft] = useState<number | null>(null);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const sessionCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const sessionHeartbeat = useRef<NodeJS.Timeout | null>(null);

  // Session refresh function
  const refreshSession = useCallback(async () => {
    try {
      setIsRefreshingSession(true);
      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        setSessionValid(false);
        return false;
      }

      setSessionValid(true);
      return true;
    } catch (err) {
      setSessionValid(false);
      return false;
    } finally {
      setIsRefreshingSession(false);
    }
  }, []);

  // Session validation function
  const validateSession = useCallback(async () => {
    const { data, error } = await supabase.auth.getSession();
    
    if (error || !data.session) {
      setSessionValid(false);
      return false;
    }
    
    // Calculate time until session expires (sessions typically last 1 hour)
    const expiresAt = data.session.expires_at;
    if (expiresAt) {
      const now = Math.floor(Date.now() / 1000);
      const timeLeft = expiresAt - now;
      setSessionTimeLeft(timeLeft > 0 ? timeLeft : 0);
      
      // If less than 5 minutes left, try to refresh
      if (timeLeft < 300 && timeLeft > 0) {
        return await refreshSession();
      }
    }
    
    setSessionValid(true);
    return true;
  }, [refreshSession]);

  // Session heartbeat to keep it alive
  const startSessionHeartbeat = useCallback(() => {
    // Clear existing intervals
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
    }
    if (sessionHeartbeat.current) {
      clearInterval(sessionHeartbeat.current);
    }

    // Check session every 30 seconds
    sessionCheckInterval.current = setInterval(validateSession, 30000);
    
    // Heartbeat every 5 minutes to keep session alive
    sessionHeartbeat.current = setInterval(async () => {
      await supabase.auth.getUser(); // Light call to keep session active
    }, 300000); // 5 minutes
  }, [validateSession]);

  useEffect(() => {
    const validateResetSession = async () => {
      // Check for error parameters in URL first
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const urlParams = new URLSearchParams(window.location.search);
      const error = hashParams.get('error') || urlParams.get('error');
      const errorCode = hashParams.get('error_code') || urlParams.get('error_code');
      const errorDesc = hashParams.get('error_description') || urlParams.get('error_description');
      
      if (error) {
        let errorMessage = "This reset link is invalid or has expired. Please request a new password reset.";
        
        if (errorCode === 'otp_expired') {
          errorMessage = "This reset link has expired. Please request a new password reset.";
        } else if (errorDesc) {
          errorMessage = errorDesc;
        }
        
        toast({
          title: "Invalid Reset Link",
          description: errorMessage,
          variant: "destructive",
        });
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      // Check for auth tokens in URL
      const accessToken = hashParams.get('access_token') || urlParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || urlParams.get('refresh_token');
      const tokenType = hashParams.get('token_type') || urlParams.get('token_type');
      
      // Support code-exchange flow for some environments
      const code = urlParams.get('code');
      if (code) {
        try {
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            toast({
              title: "Invalid Reset Link",
              description: "This reset link is invalid or has expired. Please request a new password reset.",
              variant: "destructive",
            });
            setTimeout(() => navigate('/forgot-password'), 2000);
            return;
          }
          if (data.session) {
            setSessionValid(true);
            startSessionHeartbeat();
          }
          return;
        } catch (err) {
          toast({
            title: "Invalid Reset Link",
            description: "This reset link is invalid or has expired. Please request a new password reset.",
            variant: "destructive",
          });
          setTimeout(() => navigate('/forgot-password'), 2000);
          return;
        }
      }

      // Wait for Supabase's detectSessionInUrl to process tokens (up to 3 seconds)
      let sessionFound = false;
      const maxAttempts = 15;
      const delay = 200;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (!sessionError && data.session) {
          sessionFound = true;
          setSessionValid(true);
          
          // Calculate initial time left
          if (data.session.expires_at) {
            const now = Math.floor(Date.now() / 1000);
            const timeLeft = data.session.expires_at - now;
            setSessionTimeLeft(timeLeft > 0 ? timeLeft : 0);
          }
          
          // Start heartbeat to keep session alive
          startSessionHeartbeat();
          break;
        }
        
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      if (!sessionFound) {
        setSessionValid(false);
        toast({
          title: "Invalid Reset Link",
          description: "This reset link is invalid, expired, or has already been used. Please request a new password reset. Try using an incognito/private browser window.",
          variant: "destructive",
        });
        setTimeout(() => navigate('/forgot-password'), 3000);
      }
    };

    validateResetSession();
    
    // Cleanup intervals on unmount
    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
      if (sessionHeartbeat.current) {
        clearInterval(sessionHeartbeat.current);
      }
    };
  }, [navigate, toast, t, searchParams, startSessionHeartbeat]);

  // Request new reset link
  const requestNewResetLink = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const userEmail = data.session?.user?.email;
    
    if (userEmail) {
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (!error) {
        toast({
          title: "New Reset Link Sent",
          description: "A new password reset link has been sent to your email.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send new reset link. Please try again.",
          variant: "destructive",
        });
      }
    }
    
    navigate('/forgot-password');
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: t("auth.resetPassword.toast.mismatch.title"),
        description: t("auth.resetPassword.toast.mismatch.desc"),
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: t("auth.resetPassword.toast.tooShort.title"),
        description: t("auth.resetPassword.toast.tooShort.desc"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Refresh session before attempting password update
      const refreshSuccess = await refreshSession();

      if (!refreshSuccess) {
        toast({
          title: "Session Expired",
          description: "Your password reset session has expired. Please request a new password reset link.",
          variant: "destructive",
        });
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      // Step 2: Double-check session validity
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        toast({
          title: "Session Expired",
          description: "Your password reset session has expired. Please request a new password reset link.",
          variant: "destructive",
        });
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      // Step 3: Update password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        const isSessionMissing = msg.includes('session') || msg.includes('auth session missing') || msg.includes('invalid') || msg.includes('expired');

        if (isSessionMissing) {
          toast({
            title: "Session Expired",
            description: "Your session expired during the password update. Please request a new reset link.",
            variant: "destructive",
          });
          setTimeout(() => navigate('/forgot-password'), 2000);
        } else {
          toast({
            title: t("auth.resetPassword.toast.updateError.title"),
            description: t("auth.resetPassword.toast.genericError.desc"),
            variant: "destructive",
          });
        }
        return;
      }

      toast({
        title: t("auth.resetPassword.toast.success.title"),
        description: t("auth.resetPassword.toast.success.desc"),
      });

      navigate('/login');
      
    } catch (error) {
      toast({
        title: t("auth.resetPassword.toast.genericError.title"),
        description: t("auth.resetPassword.toast.genericError.desc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4">
      <Card className="w-full max-w-md shadow-elegant border-border/50">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-primary/10 rounded-full">
              <Stethoscope className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">{t("auth.resetPassword.title")}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {t("auth.resetPassword.instructions")}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Session Status Indicator */}
          {sessionValid === false && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>Your reset session has expired.</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={requestNewResetLink}
                  className="ml-2"
                >
                  Get New Link
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {sessionValid === true && sessionTimeLeft !== null && sessionTimeLeft < 600 && (
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  Session expires in {Math.floor(sessionTimeLeft / 60)}m {sessionTimeLeft % 60}s
                </span>
                {isRefreshingSession ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshSession}
                    className="ml-2"
                  >
                    Extend
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.resetPassword.password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("auth.resetPassword.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10 transition-all duration-300 focus:shadow-medical"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("auth.resetPassword.confirmPassword")}</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="pr-10 transition-all duration-300 focus:shadow-medical"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full"
              disabled={isLoading || sessionValid === false}
            >
              {isLoading ? t("auth.resetPassword.updating") : t("auth.resetPassword.updateButton")}
            </Button>
            
            {sessionValid === false && (
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Session expired? Reset links are single-use and expire quickly.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={requestNewResetLink}
                  className="w-full"
                >
                  Request New Reset Link
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};